import React, { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { CustomProperty, CustomPropertyType, CreateCustomPropertyContract, RoleType, EmployeeType } from "winslow-core";
import { apiCreateCustomProperty, apiGetCustomPropertiesForWorkspace } from "../../../services/EmployeeRecordService";
import { Button } from "../../../ds2/components";
import { ActionLink } from "../../../ds2/components/ActionLink";
import { X, Plus } from "lucide-react";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../../config";
import { SETTINGS_DEFAULT, SETTINGS_DISPLAY } from "../app-customize/customPropertyConfig";
import { SETTINGS_COMPONENTS } from "../app-customize/AppCustomPropertyNew";
import { GenericSelect, GenericSelectOption } from "../../shared/GenericSelect";
import { MySwitch } from "../../shared/MySwitch";
import { AppCustomPropertySettings } from "../app-customize/AppCustomPropertySettings";
import "./AddPropertyModal.css";

type AddPropertyModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onAddProperty: (property: CustomProperty) => void;
  existingPropertyLabels: string[];
  workspaceId: string;
};

export const AddPropertyModal = ({
  isOpen,
  onClose,
  onAddProperty,
  existingPropertyLabels,
  workspaceId,
}: AddPropertyModalProps) => {
  const [availableProperties, setAvailableProperties] = useState<CustomProperty[]>([]);
  const [showNewPropertyForm, setShowNewPropertyForm] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedProperty, setSelectedProperty] = useState<CustomProperty | null>(null);

  useEffect(() => {
    if (isOpen && workspaceId) {
      loadAvailableProperties();
      setSelectedProperty(null);
    }
  }, [isOpen, workspaceId, existingPropertyLabels]);

  const loadAvailableProperties = async () => {
    try {
      setLoading(true);
      const properties = await apiGetCustomPropertiesForWorkspace(workspaceId);
      if (properties) {
        // Filter out properties that are already in the vibe process
        const filtered = properties.filter(
          (prop) => !existingPropertyLabels.includes(prop.label)
        );
        setAvailableProperties(filtered);
        // Set the first property as default selection
        if (filtered.length > 0) {
          setSelectedProperty(filtered[0]);
        }
      }
    } catch (error) {
      console.error("Error loading available properties:", error);
      toast("Failed to load available properties.", toastFail);
    } finally {
      setLoading(false);
    }
  };

  const handleAddExistingProperty = (property: CustomProperty) => {
    onAddProperty(property);
    onClose();
  };

  // Convert available properties to GenericSelect options
  const propertyOptions: GenericSelectOption[] = availableProperties.map((property) => ({
    displayName: property.display,
    value: property,
    component: SETTINGS_COMPONENTS[property.type],
  }));

  const selectedOption = selectedProperty
    ? {
        displayName: selectedProperty.display,
        value: selectedProperty,
        component: SETTINGS_COMPONENTS[selectedProperty.type],
      }
    : undefined;

  const handleCreateNewProperty = async (newPropertyData: {
    display: string;
    type: CustomPropertyType;
    description: string;
    required: boolean;
    settings: string;
    group?: string;
    visibleRoles?: any[];
    visibleEmployeeTypes?: any[];
  }) => {
    try {
      // Generate vibe property label following naming convention: vibe-{label}-{uuid}
      const baseLabel = newPropertyData.display.toLowerCase().replace(/\s/g, "_");
      const vibeLabel = `vibe-${baseLabel}-${uuidv4()}`;

      const payload: CreateCustomPropertyContract["Body"] = {
        type: newPropertyData.type,
        label: vibeLabel,
        display: newPropertyData.display,
        group: newPropertyData.group || "",
        description: newPropertyData.description || "",
        system: false,
        required: newPropertyData.required,
        hidden: false,
        isVibe: true, // Mark as vibe property
        visibleRoles: newPropertyData.visibleRoles || [RoleType.ADMIN, RoleType.MANAGER, RoleType.EMPLOYEE],
        visibleEmployeeTypes: newPropertyData.visibleEmployeeTypes || [
          EmployeeType.US_FULL_TIME,
          EmployeeType.US_PART_TIME,
          EmployeeType.US_CONTRACTOR,
          EmployeeType.INT_FULL_TIME,
        ],
        settings: newPropertyData.settings || JSON.stringify(SETTINGS_DEFAULT[newPropertyData.type] || {}),
        workspaceId,
      };

      const createdProperty = await apiCreateCustomProperty(payload);

      if (!createdProperty) {
        toast("Failed to create property.", toastFail);
        return;
      }

      toast("Property created successfully!", toastSuccess);
      onAddProperty(createdProperty);
      setShowNewPropertyForm(false);
      onClose();
    } catch (error) {
      console.error("Error creating property:", error);
      toast("Failed to create property.", toastFail);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="add-property-modal-overlay" onClick={onClose}>
      <div className="add-property-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="add-property-modal-header">
          <h2>Add Property</h2>
          <button className="add-property-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="add-property-modal-body">
          {!showNewPropertyForm ? (
            <>
              <div className="add-property-modal-section">
                <div className="add-property-modal-section-header">
                  <h3>Existing Properties</h3>
                  <ActionLink
                    variant="secondary"
                    size="md"
                    leftIcon={<Plus size={16} />}
                    label="Create New Property"
                    onClick={() => setShowNewPropertyForm(true)}
                    style={{ cursor: "pointer" }}
                  />
                </div>
                {loading ? (
                  <div className="add-property-modal-loading">Loading...</div>
                ) : availableProperties.length > 0 ? (
                  <div className="add-property-modal-dropdown-container">
                    <GenericSelect
                      options={propertyOptions}
                      selected={selectedOption}
                      onChange={(value: CustomProperty) => {
                        setSelectedProperty(value);
                        handleAddExistingProperty(value);
                      }}
                      maxHeight="132px"
                    />
                  </div>
                ) : (
                  <div className="add-property-modal-empty">
                    No available properties. Create a new one below.
                  </div>
                )}

                
              </div>
              <div className="add-property-modal-section-footer">
                <Button
                  variant="secondary"
                  size="md"
                  label="Close"
                  onClick={onClose}
                />
              </div>
            </>
          ) : (
            <div className="add-property-modal-new-form">
              <h3>Create New Property</h3>
              <NewPropertyForm
                onSave={handleCreateNewProperty}
                onCancel={() => setShowNewPropertyForm(false)}
                workspaceId={workspaceId}
              />
            </div>
          )}
          
        </div>
      </div>
    </div>
  );
};

// Simplified form for creating new properties within the modal
type NewPropertyFormProps = {
  onSave: (data: {
    display: string;
    type: CustomPropertyType;
    description: string;
    required: boolean;
    settings: string;
    group?: string;
    visibleRoles?: any[];
    visibleEmployeeTypes?: any[];
  }) => void;
  onCancel: () => void;
  workspaceId: string;
};

const NewPropertyForm = ({ onSave, onCancel, workspaceId }: NewPropertyFormProps) => {
  const [display, setDisplay] = useState<string>("");
  const [type, setType] = useState<CustomPropertyType>(CustomPropertyType.SHORT);
  const [description, setDescription] = useState<string>("");
  const [required, setRequired] = useState<boolean>(false);
  const [settings, setSettings] = useState<any>(SETTINGS_DEFAULT[CustomPropertyType.SHORT]);
  const [disabled, setDisabled] = useState<boolean>(false);

  // Update settings when type changes
  useEffect(() => {
    setSettings(SETTINGS_DEFAULT[type] || {});
    setDisabled(false);
  }, [type]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!display.trim()) {
      toast("Display name is required.", toastFail);
      return;
    }

    onSave({
      display: display.trim(),
      type,
      description: description.trim(),
      required,
      settings: JSON.stringify(settings || {}),
      visibleRoles: [RoleType.ADMIN, RoleType.MANAGER, RoleType.EMPLOYEE],
      visibleEmployeeTypes: [
        EmployeeType.US_FULL_TIME,
        EmployeeType.US_PART_TIME,
        EmployeeType.US_CONTRACTOR,
        EmployeeType.US_VENDOR,
        EmployeeType.INT_CONTRACTOR,
        EmployeeType.INT_FULL_TIME,
      ],
    });
  };

  // Get property type options - formatted like AppCustomPropertyNew
  const typeOptions: GenericSelectOption[] = Object.keys(CustomPropertyType)
    .sort((a: string, b: string) => {
      return SETTINGS_DISPLAY[a as CustomPropertyType].toLowerCase().localeCompare(
        SETTINGS_DISPLAY[b as CustomPropertyType].toLowerCase()
      );
    })
    .map((key) => {
      return {
        displayName: SETTINGS_DISPLAY[key as CustomPropertyType],
        value: key,
        component: SETTINGS_COMPONENTS[key as CustomPropertyType],
      };
    });

  const selectedTypeOption = typeOptions.find((option) => option.value === type);

  return (
    <form onSubmit={handleSubmit} className="new-property-form">
      <div className="new-property-form-wrapper">
        <div className="new-property-form-left">
          <div className="new-property-form-field">
            <label>Display Name *</label>
            <input
              type="text"
              value={display}
              onChange={(e) => setDisplay(e.target.value)}
              placeholder="Enter property name"
              required
              autoFocus
            />
          </div>

          <div className="new-property-form-field">
            <label>Type</label>
            <GenericSelect
              selected={selectedTypeOption}
              options={typeOptions}
              onChange={(value: CustomPropertyType) => {
                setType(value);
              }}
              maxHeight="200px"
            />
          </div>

          <div className="new-property-form-field">
            <label>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description (optional)"
              rows={3}
            />
          </div>
        </div>

        <div className="new-property-form-right">
          <div className="new-property-form-group">
            <label>Required</label>
            <MySwitch
              id="required"
              checked={required}
              onChange={setRequired}
            />
          </div>

          <AppCustomPropertySettings
            type={type}
            settings={settings}
            onChange={(newSettings, disabledState) => {
              setSettings({ ...newSettings });
              setDisabled(disabledState);
            }}
          />
        </div>
      </div>

      <div className="new-property-form-actions">
        <Button
          variant="secondary"
          size="md"
          label="Cancel"
          onClick={onCancel}
          type="button"
        />
        <Button
          variant="primary"
          size="md"
          label="Create"
          type="submit"
          disabled={!display.trim() || disabled}
        />
      </div>
    </form>
  );
};

